import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { useQuery } from "@tanstack/react-query";
import { useActionFolders } from "Hooks/useActionFolders";
import { useTranslation } from "react-i18next";
import { FiActivity } from "react-icons/fi";

import { Color } from "../../../../../../components/Form/Button";
import Access from "../../../../../../components/Generic/Access/ViewMode";
import Files from "../../../../../../components/Generic/Files";
import History from "../../../../../../components/Generic/History";
import Instructions from "../../../../../../components/Generic/Instructions";
import SideViewComponent, {
  Navigation,
  PropHandles,
} from "../../../../../../components/Generic/ViewJobComponent";
import { useAppState } from "../../../../../../context/StateContext";
import { Order } from "../../../../../../models/order";
import { RoomJob } from "../../../../../../models/sales";
import { User } from "../../../../../../models/user";
import OrderService from "../../../../../../services/order.service";
import { ResourceName } from "../../../../../../util/enums";
import { getOrderFolder } from "../../../../../../util/getFolders";
import getItemsToHistory from "../../../../../../util/getHistory";
import { getOrderCustomerDisplayName } from "../../../../../../util/names";
import Details from "../../../Operations/Maintenance/Details/ViewMode";
import OrderInventory from "../../../Operations/Maintenance/Inventory";
import SkeletonDetails from "../../../Operations/Maintenance/skeleton";
import TasksTab from "../../../Operations/Maintenance/Tasks";
import WaybillTab from "../../../Operations/Maintenance/Waybill";

interface OrderViewComponentProps {}

export interface OrderViewComponentHandler {
  open: (id: number) => void;
}

const OrderViewComponent: ForwardRefRenderFunction<
  OrderViewComponentHandler,
  OrderViewComponentProps
> = (_props, ref) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const sideViewRef = useRef<PropHandles>(null);

  const [orderId, setOrderId] = useState<number>();
  const [folderType, setFolderType] = useState<string>();
  const [order, setOrder] = useState<Order>();

  const { getOrderFolders, getQuoteFolders } = useActionFolders({ jobId: orderId });

  const { data: orderFolders, isFetching: isOrderFolders } = getOrderFolders();
  const { data: quoteFolders, isFetching: isQuoteFolders } = getQuoteFolders();

  const onOpenSideView = (id: number) => {
    setOrderId(id);
    sideViewRef.current?.setOpenSideBar(true);
  };

  const onSwitchFolder = () => {
    setFolderType((prev) =>
      prev === ResourceName.ORDERS ? ResourceName.QUOTE : ResourceName.ORDERS
    );
  };

  useImperativeHandle(ref, () => ({
    open: onOpenSideView,
  }));

  const {
    refetch: getOrder,
    data: response,
    isFetching,
  } = useQuery(
    ["get.order.by.id", orderId],
    () =>
      OrderService.getById(Number(orderId), {
        isSalesApp: true,
        isNewVersion: true,
      }),
    {
      enabled: !!orderId,
      refetchOnWindowFocus: false,
    }
  );

  const countMaterial = (): number => {
    let count = 0;

    if (response && response.data) {
      response.data.inventory?.forEach((inventory: RoomJob) => {
        if (inventory.materials.length) count += 1;
      });
    }

    return count;
  };

  const getSideBarTitle = useMemo(() => {
    let title = translate("order");
    const reference = response?.data.reference;
    const fullName = getOrderCustomerDisplayName(response?.data);

    if (reference) title = `${title} #${reference}`;
    if (fullName) title = `${title} - ${fullName}`;

    return title;
  }, [response, response?.data]);

  const setNavigation = useCallback(
    () => [
      { key: Navigation.DETAILS },
      { key: Navigation.INVENTORY },
      { key: Navigation.MATERIAL },
      { key: Navigation.TASKS },
      { key: Navigation.ACCESS },
      { key: Navigation.INSTRUCTIONS },
      { key: Navigation.SHIPMENT },
      { key: Navigation.FOLDERS },
    ],
    [response?.data]
  );

  const setViewTabs = useCallback(
    () => [
      {
        key: Navigation.DETAILS,
        loading: isFetching,
        isEmpty: !order,
        render: () => <Details order={order} />,
        renderLoading: () => <SkeletonDetails />,
      },
      {
        key: Navigation.INVENTORY,
        loading: isFetching,
        isEmpty:
          (!order?.inventory && !order?.actualInventory) ||
          (!order?.inventory?.length && !order?.actualInventory),
        render: () => (
          <OrderInventory
            order={order}
            inventory={order?.inventory || []}
            quoteJobId={order?.jobId || 0}
            defaultMode={order?.mode || ""}
            destinations={order?.destination?.locations || []}
            actualInventory={order?.actualInventory || []}
            callbackFunction={getOrder}
            storageBoxes={order?.storageBoxes}
            quotedViewOnly
            isOnViewMode
          />
        ),
        renderLoading: () => <SkeletonDetails />,
      },
      {
        key: Navigation.MATERIAL,
        loading: isFetching,
        isEmpty: !order?.inventory || !countMaterial(),
        render: () => <Details order={order} />,
        renderLoading: () => <SkeletonDetails />,
      },
      {
        key: Navigation.TASKS,
        loading: isFetching,
        isEmpty:
          (!order?.tasks && !order?.taskDates) ||
          (order?.tasks?.length === 0 && order?.taskDates?.length === 0),
        render: () => <TasksTab tasks={order?.tasks} taskDates={order?.taskDates} isOnViewMode />,
      },
      {
        key: Navigation.ACCESS,
        loading: isFetching,
        isEmpty: !order?.pickupAccess && !order?.deliveryAccess,
        renderLoading: () => <SkeletonDetails />,
        render: () => (
          <Access
            pickupAccess={order?.pickupAccess}
            deliveryAccess={order?.deliveryAccess}
            pickupAddress={order?.origin?.locations?.[0]}
            deliveryAddress={order?.destination?.locations?.[0]}
          />
        ),
      },
      {
        key: Navigation.INSTRUCTIONS,
        loading: isFetching,
        isEmpty: !order?.instructions || order?.instructions?.length === 0,
        renderLoading: () => <SkeletonDetails />,
        render: () => <Instructions isOnViewMode instructions={order?.instructions || []} />,
      },
      {
        key: Navigation.SHIPMENT,
        loading: isFetching,
        isEmpty: order?.shipments?.length === 0,
        render: () => <WaybillTab waybills={order?.shipments ?? []} isOnViewMode />,
      },
      {
        key: Navigation.FOLDERS,
        render: () => (
          <Files
            isOnViewMode
            inventory={folderType === ResourceName.QUOTE ? order?.inventory : undefined}
            folders={folderType === ResourceName.QUOTE ? quoteFolders : orderFolders}
            foldersDeleted={order?.foldersDeleted}
            filesDeleted={order?.filesDeleted}
            typeFolder={folderType}
            nextTypeFolder={
              folderType === ResourceName.ORDERS ? ResourceName.QUOTE : ResourceName.ORDERS
            }
            onSwitchTypeFolder={onSwitchFolder}
          />
        ),
        loading: isOrderFolders || isQuoteFolders,
      },
    ],
    [order]
  );

  const handleSetButtonsComponent = useCallback(() => {
    return [
      {
        buttonLabel: translate("seeActivity"),
        titlePage: translate("activity"),
        color: Color.SECONDARY,
        icon: <FiActivity size={16} />,
        renderLoading: () => <SkeletonDetails />,
        renderComponent: () => (
          <History
            items={getItemsToHistory(currentUser ?? ({} as User), order?.id, order?.histories)}
          />
        ),
      },
    ];
  }, [order]);

  useEffect(() => {
    if (response && response.data) setOrder(getOrderFolder(translate, response.data));
  }, [response]);

  return (
    <SideViewComponent
      ref={sideViewRef}
      job={order}
      isLoading={isFetching}
      title={getSideBarTitle}
      navigation={setNavigation()}
      tabs={setViewTabs()}
      buttonsComponent={handleSetButtonsComponent()}
    />
  );
};

export const OrderView = forwardRef(OrderViewComponent);
